import { Controller } from "stimulus"
import { DirectUpload } from "@rails/activestorage"

// Tailwind classes for a wizard message, by flash type. The layout's p.alert /
// p.notice styles only apply inside its own toast.
const FLASH_STYLES = {
  alert: "m-0 rounded-md bg-rose-50 p-3 text-base/6 text-pretty text-rose-800 ring-1 ring-rose-600/20 sm:text-sm/6",
  notice: "m-0 rounded-md bg-green-50 p-3 text-base/6 text-pretty text-green-800 ring-1 ring-green-600/20 sm:text-sm/6"
}

// The two kinds of work item step 5 offers. Both behave identically -- a list of
// toggleable entries whose ids ride to the server in a hidden field -- so the
// rendering, buffering and toggling code is written once and parameterized here.
const WORK_ITEM_KINDS = [
  {
    label: "Task",
    payloadKey: "task_types",
    listTarget: "taskList",
    emptyTarget: "noTasks",
    toggleAction: "setTaskTypes",
    bufferId: "bl_license_application_task_type_ids_buffer",
    fieldId: "bl_license_application_task_type_ids",
    paramName: "bl_license_application[task_type_ids]"
  },
  {
    label: "Inspection",
    payloadKey: "inspection_types",
    listTarget: "inspectionList",
    emptyTarget: "noInspections",
    toggleAction: "setInspectionTypes",
    bufferId: "bl_license_application_inspection_type_ids_buffer",
    fieldId: "bl_license_application_inspection_type_ids",
    paramName: "bl_license_application[inspection_type_ids]"
  }
]

/**
 * Stimulus controller for the v2 license application wizard.
 * Handles step navigation, keyboard shortcuts, form submissions,
 * business search, and multi-contact management.
 */
export default class extends Controller {
  static get targets() {
    return [
      "step", "navItem", "navCheck", "banner",
      "prevButton", "nextButton", "saveButton",
      "businessFormElement", "businessForm", "businessSearchSidebar", "businessSearchResults",
      "einField", "dbaField",
      "locationIsContact", "primaryContactForm", "contactsContainer", "additionalContacts",
      "tasksContainer", "taskList", "noTasks",
      "inspectionsContainer", "inspectionList", "noInspections",
      "documentFiles", "documentList"
    ]
  }

  get totalStepsValue() {
    return parseInt(this.data.get("totalSteps")) || 6
  }

  connect() {
    this.currentStep = 1
    this.completedSteps = new Set()
    this.businessId = null
    this.businessLocationId = null
    this.licenseTypeId = null
    this.applicationId = null
    this._hasWorkItems = false
    this._workItemsLoaded = false
    this._workItemsPromise = Promise.resolve()
    this._locationCheckPromise = null
    this._searchQuery = null
    this._step6Loaded = false
    this._step6Loading = false
    this.bindKeyboardShortcuts()
    this.bindClassificationChange()
    this.updateNavigation()
    this.adoptPreselectedLicenseType()
  }

  disconnect() {
    this.unbindKeyboardShortcuts()
    this.unbindClassificationChange()
    clearTimeout(this._searchTimeout)
  }

  // The classification is written into a hidden field by
  // bl_license_type_by_classification_controller, so nothing fires unless that
  // controller says so -- hence a listener here rather than a data-action.
  bindClassificationChange() {
    const field = document.querySelector("#bl_license_application_classification_id")
    if (!field) return

    this._classificationChangeHandler = this.handleClassificationChange.bind(this)
    field.addEventListener("change", this._classificationChangeHandler)
    this._classificationField = field
  }

  unbindClassificationChange() {
    if (!this._classificationField || !this._classificationChangeHandler) return

    this._classificationField.removeEventListener("change", this._classificationChangeHandler)
  }

  // A staffer can choose the license type first and narrow the classification
  // afterwards. The work item list is scoped by classification, so it has to be
  // fetched again -- otherwise step 5 offers a NAICS-scoped requirement as an
  // optional extra and the server seeds it anyway on submit.
  //
  // Refetches directly instead of re-running onLicenseTypeSelected, which would
  // also force step 6 to reload for a change that did not touch the license type.
  //
  // The refetch redraws the list from scratch, so it asks for the staffer's
  // optional ticks to be carried over -- the license type has not changed, and
  // silently dropping choices they already made (and rewriting the submitted
  // buffer without them) is not something a classification change should do.
  handleClassificationChange() {
    if (!this.licenseTypeId) return

    this._workItemsPromise = this.fetchWorkItems(this.licenseTypeId, {preserveSelections: true})
  }

  // --- Navigation ---

  nextStep(e) {
    if (e) e.preventDefault()
    if (this.currentStep >= this.totalStepsValue) return

    switch (this.currentStep) {
      case 1:
        this.saveAndAdvanceStep1()
        return
      case 2:
        if (this._locationCheckPromise) {
          this._locationCheckPromise.then(() => {
            this._locationCheckPromise = null
            if (!this.businessLocationId) {
              this._showFlash("alert", "Please set the business location before continuing.")
              return
            }
            this.completedSteps.add(2)
            this.showStep(3)
          })
          return
        }
        if (!this.businessLocationId) {
          this._showFlash("alert", "Please set the business location before continuing.")
          return
        }
        break
      case 3:
        this.saveAndAdvanceStep3()
        return
      case 4:
        this.saveAndAdvanceStep4()
        return
      case 5:
        this.syncWorkItemIds()
        break
    }

    this.completedSteps.add(this.currentStep)
    this.showStep(this.currentStep + 1)
  }

  previousStep(e) {
    if (e) e.preventDefault()
    if (this.currentStep <= 1) return
    var target = this.currentStep - 1
    if (target === 5 && this._workItemsLoaded && !this._hasWorkItems) target = 4
    this.showStep(target)
  }

  goToStep(e) {
    const step = parseInt(e.currentTarget.dataset.step)
    if (step === this.currentStep) return
    if (step > this.currentStep && !this.completedSteps.has(step - 1)) return
    this.showStep(step)
  }

  goToStepKeyboard(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      this.goToStep(e)
    }
  }

  showStep(stepNumber) {
    // Sync task type selections when leaving step 5 via any navigation path
    if (this.currentStep === 5 && stepNumber !== 5) {
      this.syncWorkItemIds()
    }

    // When entering step 2, check if business already has a location
    if (stepNumber === 2 && this.businessId && !this.businessLocationId) {
      this._locationCheckPromise = this._checkExistingLocation()
    } else {
      this._locationCheckPromise = null
    }

    // When entering step 6 (Revenue & Fees), ensure step 6 content is loaded
    if (stepNumber === this.totalStepsValue && this.applicationId) {
      this._ensureStep6Loaded()
    }

    this.stepTargets.forEach((el, index) => {
      el.classList.toggle("hidden", index + 1 !== stepNumber)
    })

    this.currentStep = stepNumber
    this.updateNavigation()
    this.clearBanner()
    this._clearFlash()
  }

  updateNavigation() {
    this.navItemTargets.forEach((item, index) => {
      const step = index + 1
      const isCurrent = step === this.currentStep
      item.classList.toggle("selected", isCurrent)
      item.classList.toggle("cursor-pointer", this.completedSteps.has(step) || isCurrent)
      if (isCurrent) {
        item.setAttribute("aria-current", "step")
      } else {
        item.removeAttribute("aria-current")
      }
    })

    this.navCheckTargets.forEach((check, index) => {
      check.classList.toggle("hidden", !this.completedSteps.has(index + 1))
    })

    // Hide tasks nav item (step 5, index 4) when no task types available
    if (this.navItemTargets.length >= 5) {
      this.navItemTargets[4].classList.toggle("hidden", !this._hasWorkItems)
    }

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("hidden", this.currentStep <= 1)
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", this.currentStep >= this.totalStepsValue)
    }

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.toggle("hidden", this.currentStep !== this.totalStepsValue)
    }
  }

  // --- Keyboard Shortcuts ---

  bindKeyboardShortcuts() {
    this._keyHandler = (e) => {
      if (e.ctrlKey && e.key === ".") {
        e.preventDefault()
        this.nextStep()
      } else if (e.ctrlKey && e.key === ",") {
        e.preventDefault()
        this.previousStep()
      } else if (e.ctrlKey && e.key === "Enter") {
        e.preventDefault()
        this.submitCurrentStep()
      } else if (e.key === "Escape") {
        const active = document.activeElement
        const isInFormControl = active && (active.tagName === "INPUT" || active.tagName === "SELECT" || active.tagName === "TEXTAREA")
        if (!isInFormControl) {
          e.preventDefault()
          this.cancelWizard()
        }
      }
    }
    document.addEventListener("keydown", this._keyHandler)
  }

  unbindKeyboardShortcuts() {
    if (this._keyHandler) {
      document.removeEventListener("keydown", this._keyHandler)
    }
  }

  submitCurrentStep() {
    if (this.hasSaveButtonTarget && !this.saveButtonTarget.classList.contains("hidden")) {
      this.saveButtonTarget.click()
      return
    }
    const nextBtn = this.nextButtonTarget
    if (nextBtn && !nextBtn.classList.contains("hidden")) {
      nextBtn.click()
    }
  }

  cancelWizard() {
    if (confirm("Are you sure you want to cancel? Any unsaved progress will be lost.")) {
      window.history.back()
    }
  }

  preventFormSubmit(e) {
    e.preventDefault()
  }

  // --- Banner ---

  showBanner(message, linkUrl, linkText) {
    if (!this.hasBannerTarget) return

    this.bannerTarget.textContent = ""

    const messageSpan = document.createElement("span")
    messageSpan.textContent = message
    this.bannerTarget.appendChild(messageSpan)

    if (linkUrl && linkText) {
      this.bannerTarget.appendChild(document.createTextNode(" "))

      const linkEl = document.createElement("a")
      linkEl.href = linkUrl
      linkEl.target = "_blank"
      linkEl.rel = "noopener noreferrer"
      linkEl.className = "underline font-medium text-green-700 hover:text-green-900"
      linkEl.textContent = linkText
      this.bannerTarget.appendChild(linkEl)

      this.bannerTarget.appendChild(document.createTextNode(" "))

      const trailingSpan = document.createElement("span")
      trailingSpan.className = "text-green-600"
      trailingSpan.textContent = "or continue to the next step."
      this.bannerTarget.appendChild(trailingSpan)
    }

    this.bannerTarget.classList.remove("hidden")
  }

  clearBanner() {
    if (!this.hasBannerTarget) return
    this.bannerTarget.classList.add("hidden")
    this.bannerTarget.textContent = ""
  }

  // --- Step 1: Business Creation ---

  saveAndAdvanceStep1() {
    if (this.businessId) {
      this.completedSteps.add(1)
      this.showStep(2)
      return
    }

    const nameField = document.getElementById("business_business_name")
    if (!nameField || nameField.value.trim().length === 0) {
      this._showFlash("alert", "Please enter a business name.")
      return
    }

    this.createBusiness()
  }

  createBusiness(e) {
    if (e) e.preventDefault()
    if (!this.hasBusinessFormElementTarget) return

    const form = this.businessFormElementTarget
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    const formData = new FormData(form)
    const residencyField = document.getElementById("bl_license_application_residency")
    const businessIsLocalField = document.getElementById("business_business_is_local")
    if (residencyField && businessIsLocalField) {
      residencyField.checked = businessIsLocalField.checked
    }

    fetch(form.action, {
      method: form.method,
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(response => {
        if (!response.ok) {
          const contentType = response.headers.get("content-type") || ""
          if (contentType.includes("application/json")) {
            return response.json().then(data => { throw new Error(this._normalizeErrors(data.errors || data) || "Error creating business") })
          }
          throw new Error("Error creating business")
        }
        return response.json()
      })
      .then(data => {
        this.businessId = data.business.id

        const businessIdField = document.querySelector("#business_location_business_id")
        if (businessIdField) businessIdField.value = data.business.id

        this.createEinRecord(data.business.id)
        this.createDbaRecord(data.business.id)
        this.uploadBusinessDocuments(data.business.id)

        this.completedSteps.add(1)
        this.showStep(2)
        this.showBanner(
          `Business "${data.business.business_name || ""}" has been created.`,
          `/businesses/${data.business.id}`,
          "View Business"
        )
      })
      .catch(error => {
        console.error("Error creating business:", error)
        this._showFlash("alert", error.message)
      })
  }

  createEinRecord(businessId) {
    if (!this.hasEinFieldTarget) return
    const ein = this.einFieldTarget.value.trim()
    if (ein.length === 0) return

    const piiTypeId = this.einFieldTarget.dataset.piiTypeId
    if (!piiTypeId) {
      console.warn("EIN field missing data-pii-type-id attribute")
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    fetch("/pii_data", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        pii_datum: { dataset: ein, pii_type_id: piiTypeId, piiable_type: "Business", piiable_id: businessId }
      })
    })
      .then(response => {
        if (!response.ok) console.warn("EIN creation returned status:", response.status)
      })
      .catch(error => console.error("Error creating EIN:", error))
  }

  createDbaRecord(businessId) {
    if (!this.hasDbaFieldTarget) return
    const dba = this.dbaFieldTarget.value.trim()
    if (dba.length === 0) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    fetch(`/businesses/${businessId}/business_aliases`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        business_alias: { alias: dba, license: "1", license_listing: "1", business_id: businessId }
      })
    })
      .then(response => {
        if (!response.ok) console.warn("DBA creation returned status:", response.status)
      })
      .catch(error => console.error("Error creating DBA:", error))
  }

  searchBusinesses(e) {
    const query = e.target.value
    if (query.length < 3) {
      this._searchQuery = null
      if (this.hasBusinessSearchSidebarTarget) this.businessSearchSidebarTarget.classList.add("hidden")
      return
    }

    this._searchQuery = query
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      var requestedQuery = query
      fetch(`/licensing/business_searches.json?name=${encodeURIComponent(query)}`, { credentials: "same-origin" })
        .then(response => {
          if (!response.ok) throw new Error("Search request failed")
          return response.json()
        })
        .then(data => {
          if (this._searchQuery !== requestedQuery) return
          if (!this.hasBusinessSearchResultsTarget) return
          const results = (data.businesses || data || []).slice(0, 10)

          this.businessSearchResultsTarget.textContent = ""

          results.forEach(b => {
            const li = document.createElement("li")
            li.className = "p-2 hover:bg-gray-100 rounded"

            const link = document.createElement("a")
            link.href = `/businesses/${b.id}`
            link.target = "_blank"
            link.rel = "noopener noreferrer"
            link.className = "text-purple-600 hover:text-purple-800"
            link.textContent = b.business_name || b.name || ""

            const span = document.createElement("span")
            span.className = "text-xs text-gray-400 ml-2"
            span.textContent = b.formation_type || ""

            li.appendChild(link)
            li.appendChild(span)
            this.businessSearchResultsTarget.appendChild(li)
          })

          if (this.hasBusinessSearchSidebarTarget) this.businessSearchSidebarTarget.classList.remove("hidden")
        })
        .catch(() => {
          if (this.hasBusinessSearchSidebarTarget) this.businessSearchSidebarTarget.classList.add("hidden")
        })
    }, 300)
  }

  // --- Step 2: Business Location ---

  setBusinessLocation(event) {
    event.preventDefault()
    const form = event.target.tagName === "FORM" ? event.target : event.target.form
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    const formData = new FormData(form)

    fetch(form.action, {
      method: form.method,
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(response => {
        const contentType = response.headers.get("content-type") || ""

        if (!response.ok) {
          if (contentType.includes("application/json")) {
            return response.json().then(data => { throw new Error(this._normalizeErrors(data.errors || data) || "Error setting location") })
          }
          throw new Error("Error setting location")
        }

        if (response.redirected || !contentType.includes("application/json")) {
          throw new Error("Unable to set location. Please try again.")
        }

        return response.json()
      })
      .then(data => {
        this.businessLocationId = data.licensable_id

        const licensableIdField = document.querySelector("#bl_license_application_licensable_id")
        const licensableTypeField = document.querySelector("#bl_license_application_licensable_type")
        if (licensableIdField) {
          licensableIdField.value = data.licensable_id
          licensableTypeField.value = data.licensable_type
        }

        const licensableSubmitButton = document.querySelector(".wizard-licensable-submit")
        if (licensableSubmitButton) {
          licensableSubmitButton.classList.add("hidden")
        }
        this.showBanner("Business location set.", `/business_locations/${data.licensable_id}`, "View Location")
      })
      .catch(error => {
        console.error("Error setting location:", error)
        this._showFlash("alert", error.message)
      })
  }

  // --- Step 3: Create Application & Advance ---

  saveAndAdvanceStep3() {
    if (!this.licenseTypeId) {
      this._showFlash("alert", "Please select a license type before continuing.")
      return
    }

    var isUpdate = !!this.applicationId

    var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    var formData = new FormData()

    // Core application fields
    formData.append("bl_license_application[license_type_id]", this.licenseTypeId)
    if (!isUpdate) {
      formData.append("bl_license_application[current_units]", "0.0")
    }

    if (this.businessLocationId) {
      formData.append("bl_license_application[licensable_id]", this.businessLocationId)
      formData.append("bl_license_application[licensable_type]", "BusinessLocation")
    }

    // Classification from step 3 form
    var classField = document.querySelector("#bl_license_application_classification_id")
    if (classField && classField.value) {
      formData.append("bl_license_application[classification_id]", classField.value)
    }

    // License period from step 6 form if available
    var expField = document.querySelector("[name='bl_license_application[for_license_expiring_on]']")
    if (expField && expField.value) {
      formData.append("bl_license_application[for_license_expiring_on]", expField.value)
    }

    // For updates, include _method=patch in FormData
    if (isUpdate) {
      formData.append("_method", "patch")
    }

    var url = isUpdate
      ? "/bl_license_applications/" + this.applicationId
      : "/bl_license_applications"

    var self = this
    return fetch(url, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(function(response) {
        if (!response.ok) {
          var contentType = response.headers.get("content-type") || ""
          if (contentType.indexOf("application/json") !== -1) {
            return response.json().then(function(data) {
              throw new Error(self._normalizeErrors(data.errors || data) || "Error saving application")
            })
          }
          throw new Error("Error saving application")
        }
        return response.json()
      })
      .then(function(data) {
        self.applicationId = data.id

        // Reload step 6 with full application context (form, fees, custom fields)
        self.reloadStep6(data.id)

        self.completedSteps.add(3)
        self.showStep(4)
        self.showBanner(
          isUpdate ? "Application updated." : "Application created.",
          "/bl_license_applications/" + data.id + "/edit",
          "View Application"
        )
      })
      .catch(function(error) {
        console.error("Error saving application:", error)
        self._showFlash("alert", error.message)
      })
  }

  // --- Step 3: License Type Selection ---

  handleLicenseTypeChange(e) {
    this.onLicenseTypeSelected(e.target.value)
  }

  // The server preselects a license type on step 3, and a default fires no
  // change event -- without this, Next on step 3 refuses the type on screen
  // until the staffer picks another one and back.
  adoptPreselectedLicenseType() {
    const select = this.element.querySelector("#bl_license_application_license_type_id")
    if (select && select.value) this.onLicenseTypeSelected(select.value)
  }

  onLicenseTypeSelected(licenseTypeId) {
    this.licenseTypeId = licenseTypeId
    this._clearWorkItemIds()
    this._hasWorkItems = false
    this._workItemsLoaded = false
    this._workItemsPromise = Promise.resolve()
    this.updateNavigation()
    this._step6Loaded = false

    if (!licenseTypeId) return
    this._workItemsPromise = this.fetchWorkItems(licenseTypeId)
  }

  fetchWorkItems(licenseTypeId, {preserveSelections = false} = {}) {
    if (!this.hasTaskListTarget && !this.hasInspectionListTarget) return Promise.resolve()

    var requestedId = licenseTypeId
    var classificationId = this._selectedClassificationId()
    var url = `/license_types/${licenseTypeId}/work_items`
    if (classificationId) url += `?classification_id=${encodeURIComponent(classificationId)}`

    // The license type alone cannot tell two in-flight requests apart: choosing a
    // classification-search result fires setLicenseType AND setClassification, so
    // two fetches go out for the SAME type with different classifications. The
    // unscoped one is issued first, and if it also lands last it repaints step 5
    // with the wrong list -- a NAICS-scoped requirement shown as optional, and the
    // buffer written from it. The token carries the classification so only the
    // newest request may render.
    var token = `${requestedId}:${classificationId || ""}`
    this._workItemsToken = token
    var current = () => this.licenseTypeId === requestedId && this._workItemsToken === token

    return fetch(url, { credentials: "same-origin" })
      .then(response => {
        if (!current()) return
        if (!response.ok) throw new Error("Error loading work items (status " + response.status + ")")
        return response.json()
      })
      .then(data => {
        if (!data || !current()) return

        var total = 0
        WORK_ITEM_KINDS.forEach(kind => {
          // Read before rendering: _renderWorkItems empties the list first.
          var keepIds = preserveSelections ? this._selectedOptionalWorkItemIds(kind) : []
          total += this._renderWorkItems(kind, data[kind.payloadKey] || [], keepIds)
        })

        this._hasWorkItems = total > 0
        this._workItemsLoaded = true
        this.updateNavigation()
      })
      .catch(error => {
        if (!current()) return
        console.error("Error fetching work items:", error)
        this._workItemsLoaded = true
        this._hasWorkItems = false
        this.updateNavigation()
        this._showFlash("alert", "Could not load tasks and inspections. They will be skipped.")
      })
  }

  // The ids the staffer has ticked by hand, so a redraw can put them back.
  // Required rows are excluded on purpose: they come back selected from the new
  // payload if they still apply, and carrying them over by id would re-select a
  // requirement the new classification no longer imposes.
  _selectedOptionalWorkItemIds(kind) {
    if (!this[`has${kind.listTarget[0].toUpperCase()}${kind.listTarget.slice(1)}Target`]) return []

    return Array.from(this[`${kind.listTarget}Target`].querySelectorAll("li.selected"))
      .map(li => li.children[0])
      .filter(button => button && button.dataset.required !== "true")
      .map(button => button.dataset.id)
  }

  // The classification chosen in step 3. A NAICS-scoped requirement only applies
  // once we know it, so it travels with the request.
  _selectedClassificationId() {
    const field = document.querySelector("#bl_license_application_classification_id")
    return field ? field.value : null
  }

  // Renders one kind's list and returns how many entries it drew. Required
  // entries start selected and cannot be toggled off -- the application will not
  // convert without them, so offering a choice would be a lie.
  _renderWorkItems(kind, items, keepIds = []) {
    // No list element means nothing was drawn and no buffer was set. Counting
    // the items anyway would advertise a step 5 that renders empty.
    if (!this[`has${kind.listTarget[0].toUpperCase()}${kind.listTarget.slice(1)}Target`]) return 0

    const listTarget = this[`${kind.listTarget}Target`]
    const emptyTargetName = `has${kind.emptyTarget[0].toUpperCase()}${kind.emptyTarget.slice(1)}Target`
    const hasEmptyTarget = this[emptyTargetName]

    listTarget.textContent = ""
    if (items.length === 0) {
      if (hasEmptyTarget) this[`${kind.emptyTarget}Target`].classList.remove("hidden")
      this._setWorkItemBuffer(kind, [])
      return 0
    }
    if (hasEmptyTarget) this[`${kind.emptyTarget}Target`].classList.add("hidden")

    const svgNS = "http://www.w3.org/2000/svg"

    items.forEach(item => {
      const li = document.createElement("li")
      li.className = "add-menu-item p-2 border rounded"
      // A required row's button is disabled, so a hover highlight on it would
      // promise an interaction that cannot happen.
      // Two separate axes: whether the row counts as included, and whether it can
      // be clicked. A carried-over tick is included but still optional.
      if (item.required || keepIds.indexOf(String(item.id)) !== -1) li.classList.add("selected")
      if (!item.required) li.classList.add("cursor-pointer", "hover:bg-purple-50")

      const btn = document.createElement("button")
      btn.setAttribute("type", "button")
      btn.className = "text-gray-800 text-left inline-flex items-center w-full p-0 m-0 text-sm bg-transparent"
      btn.dataset.action = `click->bl-wizard-v2#${kind.toggleAction}`
      btn.dataset.id = String(item.id)
      btn.dataset.description = item.name
      if (item.required) {
        btn.dataset.required = "true"
        btn.disabled = true
        btn.setAttribute("aria-disabled", "true")
        btn.classList.add("cursor-not-allowed")
      }

      const svg = document.createElementNS(svgNS, "svg")
      svg.setAttribute("class", "check-mark")
      svg.setAttribute("aria-hidden", "true")
      svg.setAttribute("fill", "currentColor")
      svg.setAttribute("viewBox", "0 0 20 20")

      const path = document.createElementNS(svgNS, "path")
      path.setAttribute("fill-rule", "evenodd")
      path.setAttribute("d", "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z")
      path.setAttribute("clip-rule", "evenodd")

      svg.appendChild(path)
      btn.appendChild(svg)

      const label = document.createElement("span")
      label.className = "work-item-label"
      btn.appendChild(label)

      li.appendChild(btn)
      this._paintWorkItem(btn)
      listTarget.appendChild(li)
    })

    this._syncWorkItemBufferFromList(kind, listTarget)
    return items.length
  }

  // Draws what a row's inclusion looks like, for both ways of getting there:
  // pre-selected because the license type requires it, and clicked by the
  // staffer. One function so they cannot drift -- before this existed, a
  // required row wore a purple checkmark reading "included" while a row the
  // staffer had just selected kept a gray checkmark and an "Add X" prompt, two
  // languages in one list. The checkmark means included; "(required)" says why
  // it cannot be taken back out.
  _paintWorkItem(button) {
    const selected = button.closest("li").classList.contains("selected")
    const name = button.dataset.description
    const suffix = button.dataset.required === "true" ? " (required)" : ""

    button.querySelector(".check-mark").setAttribute(
      "class", "check-mark w-4 h-4 inline mr-2 " + (selected ? "text-purple-600" : "text-gray-300")
    )
    button.querySelector(".work-item-label").textContent = (selected ? name : "Add " + name) + suffix
    button.setAttribute("aria-pressed", String(selected))
  }

  _syncWorkItemBufferFromList(kind, listTarget) {
    const selected = Array.from(listTarget.querySelectorAll("li.selected"))
      .map(li => li.children[0].dataset.id)
    this._setWorkItemBuffer(kind, selected)
  }

  _setWorkItemBuffer(kind, ids) {
    const bufferField = document.getElementById(kind.bufferId)
    if (bufferField) bufferField.value = ids.join(",")
  }

  // Shared by setTaskTypes and setInspectionTypes. A required entry is inert.
  _toggleWorkItem(e, kind) {
    e.preventDefault()
    const button = e.currentTarget
    if (button.dataset.required === "true") return

    button.closest("li").classList.toggle("selected")
    this._paintWorkItem(button)

    const subset = button.closest(".add-subset")
    this._syncWorkItemBufferFromList(kind, subset)
    this._showFlash("notice", `${kind.label} toggled: ${button.dataset.description}`)
  }

  uploadBusinessDocuments(businessId) {
    if (!this.hasDocumentFilesTarget) return
    var files = this.documentFilesTarget.files
    if (!files || files.length === 0) return

    var uploadUrl = this.documentFilesTarget.getAttribute("data-direct-upload-url")
    var self = this

    var uploadPromises = Array.from(files).map(function(file) {
      return new Promise(function(resolve, reject) {
        var upload = new DirectUpload(file, uploadUrl)
        upload.create(function(error, blob) {
          if (error) {
            reject(error)
          } else {
            resolve(blob.signed_id)
          }
        })
      })
    })

    Promise.all(uploadPromises)
      .then(function(signedIds) {
        var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
        var formData = new FormData()
        signedIds.forEach(function(signedId) {
          formData.append("business[documents][]", signedId)
        })
        return fetch("/businesses/" + businessId, {
          method: "PATCH",
          body: formData,
          headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
          credentials: "same-origin"
        })
      })
      .catch(function(error) { console.error("Error uploading documents:", error) })
  }

  /**
   * Fetches the full step 6 (Revenue & Fees) content from the server and
   * replaces the step 6 div. This ensures dataset fields, calculator form,
   * and custom input fields all render correctly for the persisted application.
   */
  reloadStep6(applicationId) {
    var stepIndex = this.totalStepsValue - 1
    var stepEl = this.stepTargets[stepIndex]
    if (!stepEl) return Promise.resolve()
    if (this._step6Loading) return Promise.resolve()

    this._step6Loading = true

    var self = this
    return fetch("/bl_license_applications/" + applicationId + "/wizard_step_content", {
      credentials: "same-origin",
      headers: { "Accept": "text/html" }
    })
      .then(function(response) {
        if (!response.ok) throw new Error("Error loading step 6 (status " + response.status + ")")
        return response.text()
      })
      .then(function(html) {
        stepEl.innerHTML = html
        self._step6Loaded = true
        self._step6Loading = false
      })
      .catch(function(error) {
        console.error("Error reloading step 6:", error)
        self._step6Loaded = false
        self._step6Loading = false
      })
  }

  _ensureStep6Loaded() {
    if (this._step6Loaded || this._step6Loading || !this.applicationId) return
    this.reloadStep6(this.applicationId)
  }

  // --- Step 4: Contact Management ---

  saveAndAdvanceStep4() {
    const resolveNextStep = () => {
      return this._workItemsPromise.then(() => {
        const skip = this._workItemsLoaded && !this._hasWorkItems
        return { nextStep: skip ? 6 : 5, skipTasks: skip }
      })
    }

    const advanceAfterContacts = () => {
      this._saveAllContactForms()
        .then(() => resolveNextStep())
        .then(({ nextStep, skipTasks }) => {
          this.completedSteps.add(4)
          if (skipTasks) this.completedSteps.add(5)
          this.showStep(nextStep)
        })
        .catch(error => {
          console.error("Error saving contacts:", error)
          this._showFlash("alert", "Error saving one or more contacts. Please try again.")
        })
    }

    if (!this.hasLocationIsContactTarget) {
      advanceAfterContacts()
      return
    }

    if (this.locationIsContactTarget.checked) {
      if (!this.businessId) {
        advanceAfterContacts()
        return
      }
      var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
      fetch("/businesses/" + this.businessId + "/contact_info_set_to_location", {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" }
      })
        .then(response => {
          if (!response.ok) {
            return response.json().then(data => {
              throw new Error(data.error || "Error setting contact to location")
            })
          }
          return this._saveAllContactForms()
        })
        .then(() => resolveNextStep())
        .then(({ nextStep, skipTasks }) => {
          this.completedSteps.add(4)
          if (skipTasks) this.completedSteps.add(5)
          this.showStep(nextStep)
        })
        .catch(error => {
          console.error("Error saving contacts:", error)
          this._showFlash("alert", error.message || "Error saving contact information. Please try again.")
        })
    } else {
      this._saveContactForm(this.primaryContactFormTarget, () => {
        advanceAfterContacts()
      })
    }
  }

  _saveContactForm(container, onSuccess) {
    if (!container) { if (onSuccess) onSuccess(); return }
    var form = container.querySelector("form.wizard-contact-form")
    if (!form) { if (onSuccess) onSuccess(); return }

    this._setContactableFields(form)
    var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    var formData = new FormData(form)
    var savedId = form.dataset.contactRelationId
    var url = savedId ? "/contact_relations/" + savedId : form.action
    var method = savedId ? "PATCH" : (form.method || "POST")

    fetch(url, {
      method: method,
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(function(response) {
        if (!response.ok) throw new Error("Error saving contact")
        return response.json()
      })
      .then((data) => {
        if (data.contact_relation_id) {
          form.dataset.contactRelationId = data.contact_relation_id
        }
        if (onSuccess) onSuccess()
      })
      .catch(error => {
        console.error("Error saving contact:", error)
        this._showFlash("alert", error.message)
      })
  }

  _saveAllContactForms() {
    if (!this.hasAdditionalContactsTarget || !this.businessId) return Promise.resolve()

    var forms = this.additionalContactsTarget.querySelectorAll("form.wizard-contact-form")
    if (forms.length === 0) return Promise.resolve()

    var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    var self = this
    var promises = Array.from(forms).map(function(form) {
      self._setContactableFields(form)
      var formData = new FormData(form)
      var savedId = form.dataset.contactRelationId
      var url = savedId ? "/contact_relations/" + savedId : form.action
      var method = savedId ? "PATCH" : (form.method || "POST")
      return fetch(url, {
        method: method,
        body: formData,
        headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
        credentials: "same-origin"
      }).then(function(response) {
        if (!response.ok) throw new Error("Error saving additional contact")
        return response.json()
      }).then(function(data) {
        if (data.contact_relation_id) {
          form.dataset.contactRelationId = data.contact_relation_id
        }
      })
    })

    return Promise.all(promises)
  }

  _setContactableFields(form) {
    var contactableIdField = form.querySelector("[name*='[contactable_id]']")
    var contactableTypeField = form.querySelector("[name*='[contactable_type]']")
    if (contactableIdField) contactableIdField.value = this.businessId
    if (contactableTypeField) contactableTypeField.value = "Business"
  }

  toggleContactForm(e) {
    if (!this.hasPrimaryContactFormTarget) return
    if (this.locationIsContactTarget.checked) {
      this.primaryContactFormTarget.classList.add("hidden")
    } else {
      this.primaryContactFormTarget.classList.remove("hidden")
    }
  }

  addContact() {
    if (!this.hasAdditionalContactsTarget) return
    var self = this
    return fetch("/bl_license_applications/wizard_contact_form", { credentials: "same-origin" })
      .then(function(response) {
        if (!response.ok) throw new Error("Error loading contact form")
        var contentType = response.headers.get("content-type") || ""
        if (contentType.indexOf("text/html") === -1) throw new Error("Unexpected response type loading contact form")
        return response.text()
      })
      .then(function(html) {
        self.additionalContactsTarget.insertAdjacentHTML("beforeend", html)
      })
      .catch(function(error) {
        console.error("Error loading contact form:", error)
        self._showFlash("alert", "Error adding contact form. Please try again.")
      })
  }

  removeContact(e) {
    var wrapper = e.currentTarget.closest(".wizard-additional-contact")
    if (wrapper) wrapper.remove()
  }

  // --- Step 5: Tasks ---

  setTaskTypes(e) {
    this._toggleWorkItem(e, WORK_ITEM_KINDS[0])
  }

  setInspectionTypes(e) {
    this._toggleWorkItem(e, WORK_ITEM_KINDS[1])
  }

  syncWorkItemIds() {
    const form = this.element.querySelector(".bl-license-application-form")
    if (!form) return

    WORK_ITEM_KINDS.forEach(kind => {
      const bufferField = document.getElementById(kind.bufferId)
      if (!bufferField) return

      let formField = form.querySelector(`#${kind.fieldId}`)
      if (!formField) {
        formField = document.createElement("input")
        formField.type = "hidden"
        formField.id = kind.fieldId
        formField.name = kind.paramName
        form.appendChild(formField)
      }

      formField.value = bufferField.value
    })
  }

  // --- Step 6: Application Submission ---

  saveApplication(e) {
    if (e) e.preventDefault()

    var stepIndex = this.totalStepsValue - 1
    var stepEl = this.stepTargets[stepIndex]
    if (!stepEl) return

    var form = stepEl.querySelector("form")
    if (!form) return

    var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    var formData = new FormData(form)
    var self = this

    // Sync the selected task and inspection type IDs into the form data
    WORK_ITEM_KINDS.forEach(function(kind) {
      var field = document.getElementById(kind.fieldId)
      if (field && field.value) formData.set(kind.paramName, field.value)
    })

    fetch(form.action, {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(function(response) {
        if (!response.ok) return response.json().then(function(data) { throw new Error(self._normalizeErrors(data.errors || data.error) || "Error saving application") })
        return response.json()
      })
      .then(function(data) {
        window.location.href = "/bl_license_applications/" + data.id + "/edit"
      })
      .catch(function(error) {
        console.error("Error saving application:", error)
        self._showFlash("alert", error.message)
      })
  }

  submitApplication(e) {
    e.preventDefault()
    var form = e.target
    if (form.tagName !== "FORM") form = form.querySelector("form") || form.closest("form")
    if (!form) return

    var csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute("content")
    var formData = new FormData(form)
    var self = this

    fetch(form.action, {
      method: form.method || "POST",
      body: formData,
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(function(response) {
        if (!response.ok) return response.json().then(function(data) { throw new Error(self._normalizeErrors(data.errors || data.error) || "Error creating application") })
        return response.json()
      })
      .then(function(data) {
        window.location.href = "/bl_license_applications/" + data.id + "/edit"
      })
      .catch(function(error) {
        console.error("Error submitting application:", error)
        self._showFlash("alert", error.message)
      })
  }

  // --- Helpers ---

  // The layout has a #flash-area of its own (a toast at the top of the page);
  // the wizard's messages belong in its own live region above the steps.
  _wizardFlashArea() {
    return this.element.querySelector("#flash-area")
  }

  _showFlash(type, message) {
    const flashArea = this._wizardFlashArea()
    if (!flashArea) return
    flashArea.textContent = ""
    const p = document.createElement("p")
    p.className = `${type} ${FLASH_STYLES[type] || FLASH_STYLES.notice}`
    p.textContent = message
    flashArea.appendChild(p)
    // Next sits at the bottom of a tall step, below the fold.
    if (typeof flashArea.scrollIntoView === "function") flashArea.scrollIntoView({ block: "nearest" })
  }

  _clearFlash() {
    const flashArea = this._wizardFlashArea()
    if (flashArea) flashArea.textContent = ""
  }

  _clearWorkItemIds() {
    WORK_ITEM_KINDS.forEach(kind => {
      const bufferField = document.getElementById(kind.bufferId)
      if (bufferField) bufferField.value = ""
    })
  }

  _checkExistingLocation() {
    return fetch(`/businesses/${this.businessId}/business_locations.json`, { credentials: "same-origin" })
      .then(response => {
        if (!response.ok) return
        return response.json()
      })
      .then(data => {
        if (!data) return
        const locations = Array.isArray(data) ? data : (data.business_locations || [])
        if (locations.length > 0) {
          const loc = locations[0]
          this.businessLocationId = loc.id

          const licensableIdField = document.querySelector("#bl_license_application_licensable_id")
          const licensableTypeField = document.querySelector("#bl_license_application_licensable_type")
          if (licensableIdField) {
            licensableIdField.value = loc.id
            licensableTypeField.value = "BusinessLocation"
          }

          const submitBtn = document.querySelector(".wizard-licensable-submit")
          if (submitBtn) submitBtn.classList.add("hidden")

          this.showBanner("Business location already set.", `/business_locations/${loc.id}`, "View Location")
        }
      })
      .catch(error => console.error("Error checking existing location:", error))
  }

  _normalizeErrors(errors) {
    if (!errors) return null
    if (typeof errors === "string") return errors
    if (Array.isArray(errors)) return errors.join(", ")
    if (typeof errors === "object") {
      const messages = []
      for (const key in errors) {
        const vals = Array.isArray(errors[key]) ? errors[key] : [errors[key]]
        vals.forEach(v => messages.push(key + " " + v))
      }
      return messages.join(", ")
    }
    return String(errors)
  }
}
